import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Auth, Messenger, ResourcesService } from 'src/app/service';

import { Resource } from '../schemas';
import { RemoveResourceDialog } from './remove-resource-dialog';
import { ResourceForm, ResourceFormData } from './resource-form';

@Component({
  selector: 'lcm-resource-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatPaginator,
    MatSortModule,
    MatSort,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  template: `
    <table mat-table [dataSource]="source()" [trackBy]="trackBy" matSort>
      <ng-container matColumnDef="Title">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Title</th>
        <td mat-cell *matCellDef="let resource">
          <a href="{{ resource.Url }}" target="_blank">
            {{ resource.Title }}
          </a>
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let resource">
          <div class="cell-actions justify-end">
            @if (auth.isAdmin()) {
              <button matTooltip="Edit resource" matMiniFab (click)="showForm(resource)" [disabled]="pending()">
                <mat-icon>edit</mat-icon>
              </button>
            }
            @if (auth.isAdmin()) {
              <button
                matTooltip="Delete resource"
                (click)="remove(resource)"
                matMiniFab
                [disabled]="pending()"
                color="warn"
              >
                <mat-icon>delete</mat-icon>
              </button>
            }
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columnsToShow()"></tr>

      <tr mat-row *matRowDef="let resource; columns: columnsToShow()"></tr>
    </table>

    <mat-paginator [pageSize]="10" [showFirstLastButtons]="true"></mat-paginator>
  `,
})
export class ResourceTable {
  dialog = inject(MatDialog);
  auth = inject(Auth);
  messenger = inject(Messenger);
  resourceService = inject(ResourcesService);

  paginator = viewChild.required(MatPaginator);
  sort = viewChild.required(MatSort);

  resources = input.required<Resource[]>();
  pending = input(false);

  edited = output<Resource>();
  removed = output<Resource>();

  source = computed(() => {
    const source = new MatTableDataSource(this.resources());
    source.paginator = this.paginator();
    source.sort = this.sort();
    return source;
  });

  trackBy(idx: number, row: Resource) {
    return row.Id;
  }

  columnsToShow = signal(['Title', 'actions']);

  showForm(resource: Resource) {
    this.dialog
      .open(ResourceForm, {
        data: { resource } as ResourceFormData,
      })
      .afterClosed()
      .subscribe({ next: (result) => result && this.edited.emit(resource) });
  }

  remove(resource: Resource) {
    this.dialog
      .open(RemoveResourceDialog, {
        data: { resource },
      })
      .afterClosed()
      .subscribe({
        next: (resource) => resource && this.removed.emit(resource),
      });
  }
}
